import { Stack, Typography } from "@mui/material";
import { ReactNode } from "react";
import { MaterialLimitBoundField } from "./materialLimitMatrix";

export const HARD_INEQUALITY = "≤";
export const SOFT_INEQUALITY = "⪅";
export const MATERIAL_SYMBOL = "x";
export const SLOT_WIDTH = 56;

/** Shared sx for a bound input: fixed width, monospace numerals that don't clip. */
export const slotInputSx = {
  width: SLOT_WIDTH,
  "& .MuiInputBase-input": { typography: "body2Mono" },
};

const InequalitySymbol = ({ text }: { text: string }) => (
  <Typography
    variant="caption"
    color="text.secondary"
    sx={{
      fontWeight:
        text === HARD_INEQUALITY ? "fontWeightBold" : "fontWeightRegular",
    }}
  >
    {text}
  </Typography>
);

/**
 * The shared `[min] ≤ [soft_min] ⪅ x ⪅ [soft_max] ≤ [max]` layout, with each
 * slot rendered by the caller (an editable input, or read-only text for an
 * aggregate). Sharing it keeps cells, aggregates and the bulk editor aligned.
 */
export const InequalityLayout = ({
  fields,
  renderSlot,
}: {
  fields: MaterialLimitBoundField[];
  renderSlot: (field: MaterialLimitBoundField) => ReactNode;
}) => (
  <Stack direction="row" alignItems="center" gap={0.25}>
    {fields.includes("min_mass") ? (
      <>
        {renderSlot("min_mass")}
        <InequalitySymbol text={HARD_INEQUALITY} />
      </>
    ) : null}
    {fields.includes("soft_min_mass") ? (
      <>
        {renderSlot("soft_min_mass")}
        <InequalitySymbol text={SOFT_INEQUALITY} />
      </>
    ) : null}
    <InequalitySymbol text={MATERIAL_SYMBOL} />
    {fields.includes("soft_max_mass") ? (
      <>
        <InequalitySymbol text={SOFT_INEQUALITY} />
        {renderSlot("soft_max_mass")}
      </>
    ) : null}
    {fields.includes("max_mass") ? (
      <>
        <InequalitySymbol text={HARD_INEQUALITY} />
        {renderSlot("max_mass")}
      </>
    ) : null}
  </Stack>
);
